import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-2",
  "data-testid": "activity-requests-filter-bar"
}
const _hoisted_2 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_3 = { class: "relative flex items-center" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, "Buscar por médico, CRM ou #id", -1)
const _hoisted_5 = ["value"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("kbd", {
  class: "absolute right-2 text-[11px] text-neutral50",
  "aria-hidden": "true"
}, "/", -1)
const _hoisted_7 = ["aria-expanded"]
const _hoisted_8 = {
  key: 0,
  class: "m-0 text-xs text-neutral50",
  "data-testid": "activity-requests-shifts-unavailable"
}
const _hoisted_9 = {
  key: 1,
  id: "activity-requests-more-filters",
  class: "flex flex-wrap items-start gap-6 rounded-lg border border-lightGray30 bg-white p-3",
  "data-testid": "activity-requests-more-filters-panel"
}
const _hoisted_10 = { class: "flex flex-col gap-1" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", { class: "text-xs font-bold text-neutral50" }, "Prazo personalizado", -1)
const _hoisted_12 = ["data-testid"]
const _hoisted_13 = { class: "text-xs font-bold text-neutral50" }
const _hoisted_14 = ["aria-label"]
const _hoisted_15 = ["aria-pressed", "data-testid", "onClick"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import ActiveFiltersTags from '@/components/Filters/ActiveFilters.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { ACTIVITY_REQUEST_SEARCH_DEBOUNCE_MS } from '@/constants/activity-requests.constants'
import { parseLocalDate, toDateOnly } from '@/utils/date'

import {
  countMoreFilters,
  hasActiveActivityRequestFilters
} from '../../composables/useActivityRequestFilters'
import type { ActivityRequestFilters } from '../../composables/useActivityRequestFilters'
import {
  REGISTRATION_MODE_OPTIONS,
  REQUESTED_LEG_OPTIONS,
  buildActivityRequestChips,
  chipRemovalPatch
} from '../../utils/activity-request-filter-options'
import type { ActivityRequestChip, FilterOption } from '../../utils/activity-request-filter-options'

const SHIFTS_UNAVAILABLE = 'Turnos indisponíveis para o seu perfil — filtre por escala.'

type IdKey = 'healthUnitIds' | 'scheduleIds' | 'shiftIds'
type TextKey = 'operationalGroups' | 'registrationModes' | 'requestedLeg'
type TriKey = 'hasAntecipe' | 'hasInCash' | 'reopened'


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityRequestFilterBar',
  props: {
    filters: {},
    unitOptions: {},
    scheduleOptions: {},
    shiftOptions: {},
    groupOptions: {},
    shiftOptionsUnavailable: { type: Boolean }
  },
  emits: ["update", "clear"],
  setup(__props: any, { emit }) {

const props = __props;





const TRI_STATES: Array<{ key: TriKey; label: string }> = [
  { key: 'hasAntecipe', label: 'Com Antecipe' },
  { key: 'hasInCash', label: 'Com À vista' },
  { key: 'reopened', label: 'Reaberta após rejeição' }
]
const TRI_OPTIONS: Array<{ id: 'any' | 'yes' | 'no'; label: string; value: boolean | null }> = [
  { id: 'any', label: 'Tanto faz', value: null },
  { id: 'yes', label: 'Sim', value: true },
  { id: 'no', label: 'Não', value: false }
]

const openFilter = ref<string | null>(null)
const isMoreOpen = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)

const toggleOpen = (key: string): void => {
  openFilter.value = openFilter.value === key ? null : key
}

const toggleInList = <T,>(list: readonly T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

// Chave computada vira índice `string` no TS: o cast diz que o patch é parcial dos filtros.
function onToggleId(key: IdKey, raw: string): void {
  const next = toggleInList(props.filters[key], Number(raw))
  emit('update', { [key]: next } as Partial<ActivityRequestFilters>)
}

function onToggleText(key: TextKey, raw: string): void {
  const next = toggleInList(props.filters[key] as readonly string[], raw)
  emit('update', { [key]: next } as Partial<ActivityRequestFilters>)
}

function setTri(key: TriKey, value: boolean | null): void {
  emit('update', { [key]: value } as Partial<ActivityRequestFilters>)
}

/* ---------------------------------------------------------------- busca */

const searchText = ref(props.filters.search)
let searchDebounce: ReturnType<typeof setTimeout> | null = null
const cancelSearchDebounce = (): void => {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = null
}

function onSearchInput(value: string): void {
  searchText.value = value
  cancelSearchDebounce()
  searchDebounce = setTimeout(() => {
    searchDebounce = null
    emit('update', { search: value })
  }, ACTIVITY_REQUEST_SEARCH_DEBOUNCE_MS)
}

// Mudança de fora (limpar, visão salva, URL): o campo acompanha e a busca pendente cai.
watch(
  () => props.filters.search,
  (search) => {
    if (search === searchText.value) return
    cancelSearchDebounce()
    searchText.value = search
  }
)

/** Atalho `/` (M1, balão 1): foca a busca, a menos que a pessoa já esteja digitando. */
function onGlobalKeydown(event: KeyboardEvent): void {
  if (event.key !== '/') return
  const target = event.target as HTMLElement | null
  const typing =
    target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
  if (typing) return
  event.preventDefault()
  searchInput.value?.focus()
}

onMounted(() => window.addEventListener('keydown', onGlobalKeydown))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
  cancelSearchDebounce()
})

/* ---------------------------------------------------------------- datas */

const stationDateRange = computed(() =>
  props.filters.startDate || props.filters.endDate
    ? { start: parseLocalDate(props.filters.startDate), end: parseLocalDate(props.filters.endDate) }
    : null
)

function onStationDate(range: { start: Date | null; end: Date | null }): void {
  emit('update', {
    startDate: range.start ? toDateOnly(range.start) : null,
    endDate: range.end ? toDateOnly(range.end) : range.start ? toDateOnly(range.start) : null
  })
}

const deadlineRange = computed(() =>
  props.filters.deadlineFrom || props.filters.deadlineTo
    ? {
        start: props.filters.deadlineFrom ? new Date(props.filters.deadlineFrom) : null,
        end: props.filters.deadlineTo ? new Date(props.filters.deadlineTo) : null
      }
    : null
)

/** Prazo "de/até" em dias do navegador: de 00:00 até 23:59:59.999, como instantes ISO (§4.7). */
function onDeadline(range: { start: Date | null; end: Date | null }): void {
  const start = range.start ? new Date(range.start) : null
  const end = range.end ? new Date(range.end) : start ? new Date(start) : null
  start?.setHours(0, 0, 0, 0)
  end?.setHours(23, 59, 59, 999)
  emit('update', {
    deadlineFrom: start ? start.toISOString() : null,
    deadlineTo: end ? end.toISOString() : null
  })
}

/* ---------------------------------------------------------------- chips */

const moreCount = computed(() => countMoreFilters(props.filters))
const hasActive = computed(() => hasActiveActivityRequestFilters(props.filters))

const chips = computed(() =>
  buildActivityRequestChips(props.filters, {
    units: props.unitOptions,
    schedules: props.scheduleOptions,
    shifts: props.shiftOptions
  })
)

function onRemoveChip(chip: ActivityRequestChip): void {
  emit('update', chipRemovalPatch(props.filters, chip))
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("label", _hoisted_3, [
        _hoisted_4,
        _createElementVNode("input", {
          ref_key: "searchInput",
          ref: searchInput,
          type: "search",
          value: searchText.value,
          placeholder: "Médico, CRM ou #id",
          class: "h-8 w-64 rounded-md border border-lightGray30 px-3 pr-7 text-sm",
          "data-testid": "activity-requests-search-input",
          onInput: _cache[0] || (_cache[0] = ($event: any) => (onSearchInput(($event.target as HTMLInputElement).value)))
        }, null, 40, _hoisted_5),
        _hoisted_6
      ]),
      _createVNode(SelectFilter, {
        label: "Unidades",
        "is-open": openFilter.value === 'unit',
        options: _ctx.unitOptions,
        multiple: "",
        searchable: "",
        "dropdown-width": "w-72",
        "selected-value": _ctx.filters.healthUnitIds.map(String),
        "data-testid": "activity-requests-filter-unit",
        onToggle: _cache[1] || (_cache[1] = ($event: any) => (toggleOpen('unit'))),
        onSelect: _cache[2] || (_cache[2] = ($event: any) => (onToggleId('healthUnitIds', $event)))
      }, null, 8, ["is-open", "options", "selected-value"]),
      _createVNode(SelectFilter, {
        label: "Grupo Operacional",
        "is-open": openFilter.value === 'group',
        options: _ctx.groupOptions,
        multiple: "",
        searchable: "",
        "dropdown-width": "w-64",
        "selected-value": _ctx.filters.operationalGroups,
        "data-testid": "activity-requests-filter-group",
        onToggle: _cache[3] || (_cache[3] = ($event: any) => (toggleOpen('group'))),
        onSelect: _cache[4] || (_cache[4] = ($event: any) => (onToggleText('operationalGroups', $event)))
      }, null, 8, ["is-open", "options", "selected-value"]),
      _createVNode(SelectFilter, {
        label: "Escala",
        "is-open": openFilter.value === 'schedule',
        options: _ctx.scheduleOptions,
        multiple: "",
        searchable: "",
        "dropdown-width": "w-80",
        "selected-value": _ctx.filters.scheduleIds.map(String),
        "data-testid": "activity-requests-filter-schedule",
        onToggle: _cache[5] || (_cache[5] = ($event: any) => (toggleOpen('schedule'))),
        onSelect: _cache[6] || (_cache[6] = ($event: any) => (onToggleId('scheduleIds', $event)))
      }, null, 8, ["is-open", "options", "selected-value"]),
      _createVNode(SelectFilter, {
        label: "Turno",
        "is-open": openFilter.value === 'shift',
        options: _ctx.shiftOptions,
        multiple: "",
        searchable: "",
        "dropdown-width": "w-96",
        "selected-value": _ctx.filters.shiftIds.map(String),
        title: _ctx.shiftOptionsUnavailable ? SHIFTS_UNAVAILABLE : undefined,
        "data-testid": "activity-requests-filter-shift",
        onToggle: _cache[7] || (_cache[7] = ($event: any) => (toggleOpen('shift'))),
        onSelect: _cache[8] || (_cache[8] = ($event: any) => (onToggleId('shiftIds', $event)))
      }, null, 8, ["is-open", "options", "selected-value", "title"]),
      _createVNode(SelectFilter, {
        label: "Modalidade de registro",
        "is-open": openFilter.value === 'registrationMode',
        options: _unref(REGISTRATION_MODE_OPTIONS),
        multiple: "",
        "dropdown-width": "w-80",
        "selected-value": _ctx.filters.registrationModes,
        "data-testid": "activity-requests-filter-registration-mode",
        onToggle: _cache[9] || (_cache[9] = ($event: any) => (toggleOpen('registrationMode'))),
        onSelect: _cache[10] || (_cache[10] = ($event: any) => (onToggleText('registrationModes', $event)))
      }, null, 8, ["is-open", "options", "selected-value"]),
      _createVNode(SelectFilter, {
        label: "Perna",
        "is-open": openFilter.value === 'leg',
        options: _unref(REQUESTED_LEG_OPTIONS),
        multiple: "",
        "dropdown-width": "w-40",
        "selected-value": _ctx.filters.requestedLeg,
        "data-testid": "activity-requests-filter-leg",
        onToggle: _cache[11] || (_cache[11] = ($event: any) => (toggleOpen('leg'))),
        onSelect: _cache[12] || (_cache[12] = ($event: any) => (onToggleText('requestedLeg', $event)))
      }, null, 8, ["is-open", "options", "selected-value"]),
      _createVNode(DateFilter, {
        title: "Data plantão",
        "is-range": "",
        "model-value": stationDateRange.value,
        "data-testid": "activity-requests-filter-station-date",
        onSelect: onStationDate
      }, null, 8, ["model-value"]),
      _createElementVNode("button", {
        type: "button",
        class: "h-8 rounded-md border border-lightGray30 px-3 text-sm font-bold text-primary30",
        "aria-expanded": isMoreOpen.value,
        "aria-controls": "activity-requests-more-filters",
        "data-testid": "activity-requests-more-filters-button",
        onClick: _cache[13] || (_cache[13] = ($event: any) => (isMoreOpen.value = !isMoreOpen.value))
      }, _toDisplayString(moreCount.value ? `Mais filtros (${moreCount.value})` : 'Mais filtros'), 9, _hoisted_7)
    ]),
    (_ctx.shiftOptionsUnavailable)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(SHIFTS_UNAVAILABLE)))
      : _createCommentVNode("", true),
    (isMoreOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _hoisted_11,
            _createVNode(DateFilter, {
              title: "Prazo (de/até)",
              "is-range": "",
              "model-value": deadlineRange.value,
              "data-testid": "activity-requests-filter-deadline",
              onSelect: onDeadline
            }, null, 8, ["model-value"])
          ]),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(TRI_STATES, (tri) => {
            return _createElementVNode("div", {
              key: tri.key,
              class: "flex flex-col gap-1",
              "data-testid": `activity-requests-tri-${tri.key}`
            }, [
              _createElementVNode("span", _hoisted_13, _toDisplayString(tri.label), 1),
              _createElementVNode("div", {
                class: "inline-flex rounded-full border border-lightGray30 p-0.5",
                role: "group",
                "aria-label": tri.label
              }, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(TRI_OPTIONS, (option) => {
                  return _createElementVNode("button", {
                    key: option.id,
                    type: "button",
                    class: _normalizeClass(["rounded-full px-3 py-1 text-xs", 
              _ctx.filters[tri.key] === option.value
                ? 'bg-primary30 text-white font-bold'
                : 'text-neutral50'
            ]),
                    "aria-pressed": _ctx.filters[tri.key] === option.value,
                    "data-testid": `activity-requests-tri-${tri.key}-${option.id}`,
                    onClick: ($event: any) => (setTri(tri.key, option.value))
                  }, _toDisplayString(option.label), 11, _hoisted_15)
                }), 64))
              ], 8, _hoisted_14)
            ], 8, _hoisted_12)
          }), 64))
        ]))
      : _createCommentVNode("", true),
    _createVNode(ActiveFiltersTags, {
      filters: chips.value,
      "show-clear-button": hasActive.value,
      "test-id-prefix": "activity-requests-chips",
      onRemove: onRemoveChip,
      onClearAll: _cache[14] || (_cache[14] = ($event: any) => (emit('clear')))
    }, null, 8, ["filters", "show-clear-button"])
  ]))
}
}

})